import React, { useState, useRef } from 'react';
import { Modal, Button, Upload, message, Spin } from 'antd';
import {
  UploadOutlined,
  FileExcelOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';

interface ImportDataDialogProps {
  visible: boolean;
  nodeId: string | null;
  nodeName?: string;
  onConfirm: (file: File) => Promise<void>;
  onCancel: () => void;
}

const ImportDataDialog: React.FC<ImportDataDialogProps> = ({
  visible,
  nodeId,
  nodeName,
  onConfirm,
  onCancel,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 支持的文件类型
  const acceptedFileTypes = '.csv';

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // 验证文件类型
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      if (fileExt !== 'csv') {
        message.error('请选择 CSV 格式的文件');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleConfirm = async () => {
    if (!selectedFile) {
      message.warning('请先选择要导入的文件');
      return;
    }

    setUploading(true);
    try {
      await onConfirm(selectedFile);
      // 成功后清理状态
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      message.error(error?.message || '导入失败，请重试');
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onCancel();
  };

  const getFileIcon = () => {
    return <FileExcelOutlined style={{ fontSize: 24, color: '#52c41a' }} />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <Modal
      title="导入数据"
      open={visible}
      onCancel={handleCancel}
      destroyOnHidden
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={uploading}>
          取消
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleConfirm}
          loading={uploading}
          disabled={!selectedFile}
        >
          {uploading ? '导入中...' : '开始导入'}
        </Button>,
      ]}
      width={480}
    >
      <Spin spinning={uploading} tip="正在导入数据，请稍候...">
        <div style={{ padding: '16px 0' }}>
          {/* 目标节点信息 */}
          <div
            style={{
              marginBottom: 16,
              padding: '12px 16px',
              background: '#f5f5f5',
              borderRadius: 8,
            }}
          >
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
              目标节点
            </div>
            <div style={{ fontSize: 14, fontWeight: 500, color: '#333' }}>
              {nodeName || nodeId || '未选择节点'}
            </div>
          </div>

          {/* 文件上传区域 */}
          <div
            style={{
              border: '2px dashed #d9d9d9',
              borderRadius: 8,
              padding: 24,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'border-color 0.3s',
              background: selectedFile ? '#fafafa' : '#fff',
            }}
            onClick={() => !selectedFile && fileInputRef.current?.click()}
            onDragOver={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={e => {
              e.preventDefault();
              e.stopPropagation();
              const file = e.dataTransfer.files?.[0];
              if (file) {
                const fileExt = file.name.split('.').pop()?.toLowerCase();
                if (fileExt !== 'csv') {
                  message.error('请选择 CSV 格式的文件');
                  return;
                }
                setSelectedFile(file);
              }
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptedFileTypes}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            {selectedFile ? (
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12,
                  }}
                >
                  {getFileIcon()}
                  <div style={{ textAlign: 'left' }}>
                    <div
                      style={{ fontSize: 14, fontWeight: 500, color: '#333' }}
                    >
                      {selectedFile.name}
                    </div>
                    <div style={{ fontSize: 12, color: '#999' }}>
                      CSV 文件 · {formatFileSize(selectedFile.size)}
                    </div>
                  </div>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={e => {
                      e.stopPropagation();
                      handleRemoveFile();
                    }}
                  />
                </div>
              </div>
            ) : (
              <div>
                <UploadOutlined
                  style={{ fontSize: 32, color: '#999', marginBottom: 8 }}
                />
                <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>
                  点击或拖拽文件到此处上传
                </div>
                <div style={{ fontSize: 12, color: '#999' }}>
                  仅支持 CSV 格式
                </div>
              </div>
            )}
          </div>

          {/* 说明文字 */}
          {/* <div style={{ marginTop: 16, fontSize: 12, color: '#999' }}>
            <div>说明：</div>
            <ul style={{ paddingLeft: 16, margin: '4px 0 0 0' }}>
              <li>导入的数据将基于选中的节点进行扩展</li>
              <li>系统会自动解析文件并创建相应的节点和连线</li>
              <li>新创建的元素将自动排列在目标节点周围</li>
            </ul>
          </div> */}
        </div>
      </Spin>
    </Modal>
  );
};

export default ImportDataDialog;
